import { createBrowserObservability, readBrowserEnv } from "@jfrader/observability/browser"
import { posthogOptions } from "./posthogPrivacy"

// Vite exposes VITE_* on import.meta.env ; map to what the helper expects (it strips prefix)
const viteEnv = {
  ...(import.meta.env as any),
  // ensure the keys the reader looks for
  VITE_SENTRY_DSN: (import.meta.env as any).VITE_SENTRY_DSN,
  VITE_POSTHOG_KEY: (import.meta.env as any).VITE_POSTHOG_KEY,
  VITE_POSTHOG_HOST: (import.meta.env as any).VITE_POSTHOG_HOST,
}

const env = readBrowserEnv(viteEnv as any, "VITE_")
const environment = (import.meta.env.MODE ?? "development") as string

const createLazyProxy = (loader: () => Promise<any>): any => {
  let instance: any = null
  let promise: Promise<any> | null = null
  const queue: Array<(inst: any) => void> = []

  const load = () => {
    if (!promise) {
      promise = loader().then((res) => {
        instance = res
        queue.forEach((cb) => cb(instance))
        queue.length = 0
        return res
      }).catch((err) => {
        console.error("Failed to load observability provider", err)
      })
    }
  }

  // Methods called before the provider resolves are queued and replayed once it
  // loads. `then` stays undefined so the proxy can never be taken for a thenable.
  return new Proxy({}, {
    get(_, prop: string | symbol) {
      if (prop === "then" || typeof prop === "symbol") return undefined
      return (...args: any[]) => {
        if (instance) {
          return instance[prop]?.(...args)
        }
        queue.push((inst) => inst[prop]?.(...args))
        load()
      }
    }
  })
}

export const observability = createBrowserObservability({
  appName: "jfrader-com",
  environment,
  errorReporter: env.sentryDsn
    ? createLazyProxy(() =>
        import("@jfrader/observability/providers/sentry-browser").then((m) =>
          m.createSentryBrowserErrorReporter({ dsn: env.sentryDsn!, environment })
        )
      )
    : undefined,
  analytics: env.posthogKey
    ? createLazyProxy(() =>
        import("@jfrader/observability/providers/posthog-browser").then((m) =>
          m.createPosthogBrowserAnalytics({
            key: env.posthogKey!,
            host: env.posthogHost ?? "https://us.i.posthog.com",
            redact: true,
            options: posthogOptions,
          })
        )
      )
    : undefined,
})
